// libs/shared/src/lib/services/base-sse.service.ts

import { Injectable, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';

// libs/shared/src/lib/services/access-token-provider.interface.ts

export interface BaseSseAccessTokenProvider {
  /**
   * Retrieves the current access token.
   * @returns The access token as a string.
   */
  getAccessToken(): string;
}

export interface SseEnvironment {
  apiBaseUrl: {
    web: string;
    ios: string;
    android: string;
  };
}

export interface SseEvent {
  event: string;
  data: any;
}

@Injectable()
export abstract class BaseSseService implements OnDestroy {
  // Subject to emit SSE events
  protected eventSubject = new Subject<SseEvent>();
  readonly events$$ = toSignal(this.eventSubject);

  // Reconnection parameters
  protected retryInterval = 5000; // Initial retry interval in milliseconds
  protected readonly maxRetryInterval = 60000; // Maximum retry interval
  protected lastEventId = ''; // To keep track of the last received event ID
  protected isManuallyClosed = false; // Flag to determine if the connection was manually closed

  // AbortController to manage fetch request
  private abortController: AbortController | null = null;

  protected constructor(
    protected accessTokenProvider: BaseSseAccessTokenProvider,
    protected environment: SseEnvironment
  ) {}

  /**
   * Establishes the SSE connection using fetch and ReadableStream.
   */
  public buildConnection() {
    const accessToken = this.accessTokenProvider.getAccessToken();
    const baseUrl = this.getApiBaseUrl();
    const url = `${baseUrl}/api/sse/subscribe?lastEventId=${this.lastEventId}&ngsw-bypass=true`;

    // Initialize AbortController
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Initiate the fetch request with custom headers
    fetch(url, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
        'X-Accel-Buffering': 'no', // Optional: Disable buffering if using NGINX or similar
        Authorization: `Bearer ${accessToken}`, // Include the Authorization header
      },
      credentials: 'include', // Include cookies if necessary
      signal, // Attach the abort signal
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let currentEvent: string | null = null; // Tracks the current event type

        const read = () => {
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                console.log('SSE connection closed by server.');
                if (!this.isManuallyClosed) {
                  this.scheduleReconnection();
                }
                return;
              }

              // Decode the incoming bytes into a string
              buffer += decoder.decode(value, { stream: true });
              let position = 0;
              let newlineIndex: number;

              // Process the buffer line by line
              while ((newlineIndex = buffer.indexOf('\n', position)) !== -1) {
                const line = buffer.slice(position, newlineIndex).trim();
                position = newlineIndex + 1;

                if (line.startsWith('event:')) {
                  // Extract the event type
                  currentEvent = line.replace('event:', '').trim();
                } else if (line.startsWith('data:')) {
                  // Extract and parse the data payload
                  const dataStr = line.replace('data:', '').trim();
                  let data: any;
                  try {
                    data = JSON.parse(dataStr);
                  } catch (e) {
                    data = dataStr; // Fallback to raw data if JSON parsing fails
                  }

                  // Emit the event through the subject
                  this.eventSubject.next({
                    event: currentEvent || 'message',
                    data,
                  });

                  // Handle specific events internally
                  this.handleEvent(currentEvent || 'message', data);
                } else if (line.startsWith('id:')) {
                  // Update the lastEventId for reconnection purposes
                  this.lastEventId = line.replace('id:', '').trim();
                }
                // Additional SSE fields like 'retry:' can be handled here if necessary
              }

              // Retain any incomplete lines in the buffer
              buffer = buffer.slice(position);
              read(); // Continue reading the stream
            })
            .catch(error => {
              if (error.name === 'AbortError') {
                console.log('SSE connection aborted.');
              } else {
                console.error('Error reading SSE stream:', error);
                if (!this.isManuallyClosed) {
                  this.scheduleReconnection();
                }
              }
            });
        };

        read(); // Start reading the stream
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          console.log('Fetch SSE connection aborted.');
        } else {
          console.error('Fetch SSE connection error:', error);
          if (!this.isManuallyClosed) {
            this.scheduleReconnection();
          }
        }
      });
  }

  /**
   * Gets the appropriate API base URL based on the platform.
   */
  private getApiBaseUrl(): string {
    const platform = Capacitor.getPlatform();
    switch (platform) {
      case 'ios':
        return this.environment.apiBaseUrl.ios;
      case 'android':
        return this.environment.apiBaseUrl.android;
      default:
        return this.environment.apiBaseUrl.web;
    }
  }

  /**
   * Handles specific SSE events by invoking corresponding actions.
   * Must be implemented by concrete services.
   * @param eventType - The type of the SSE event.
   * @param data - The data payload of the SSE event.
   */
  protected abstract handleEvent(eventType: string, data: any): void;

  /**
   * Schedules a reconnection attempt with exponential backoff.
   */
  protected scheduleReconnection() {
    console.log(
      `Reconnecting to SSE in ${this.retryInterval / 1000} seconds...`
    );
    setTimeout(() => {
      this.retryInterval = Math.min(
        this.retryInterval * 2,
        this.maxRetryInterval
      ); // Exponential backoff
      this.buildConnection();
    }, this.retryInterval);
  }

  /**
   * Closes the SSE connection and completes the event subject.
   */
  public closeConnection() {
    this.isManuallyClosed = true; // Prevent automatic reconnection
    if (this.abortController) {
      this.abortController.abort(); // Abort the fetch request
    }
    this.eventSubject.complete();
  }

  /**
   * Lifecycle hook that is called when the service is destroyed.
   */
  ngOnDestroy() {
    this.closeConnection();
  }

  /**
   * Exposes the SSE event stream as an Observable for components to subscribe to.
   */
  get events$(): Observable<SseEvent> {
    return this.eventSubject.asObservable();
  }
}
